import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "title", "onDelete", "shouldFocus", "isDisabled", "isError", "closeButtonProps"],
    _excluded2 = ["name", "value", "defaultValue", "allowDuplicates", "onChange", "inputProps", "children", "ChipComponent", "chipProps", "autocompleteOnly", "autocompleteExtraItems", "placeholder", "menuListProps", "dropdownProps", "isOptionVisible", "chipTitleRender", "isChipDisabled", "noResultsLabel", "showDropdownIfNoResults", "addOnBlur", "delimiter", "theme", "multiSelect"];
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Text from '../Text';
import { BaseDropdown } from '../Popover';
import MenuList from '../MenuList';
import CloseIcon from '../Icons/CloseIcon';
import { KEY_BACKSPACE, KEY_ENTER, KEY_DOWN, KEY_UP, KEY_ESC } from '../utils/kb';
import { isFunction, noop } from '../utils';
import { isDisabled, isError } from './Input';
import { ChipsWrapper, ChipWrapper, CloseButton, InputWrapper, StyledInput } from './ChipsInput.styles';
import { useCombinedRefs } from "../hooks";
import { useAutocompleteStates, useChipValues } from './ChipsInput.hooks';
import { withTheme } from 'styled-components';
var DEFAULT_PLACEHOLDER = 'Start typing here';
/**
 * Single Chip Component
 */

export var Chip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      title = _ref.title,
      onDelete = _ref.onDelete,
      shouldFocus = _ref.shouldFocus,
      isDisabled = _ref.isDisabled,
      isError = _ref.isError,
      closeButtonProps = _ref.closeButtonProps,
      props = _objectWithoutProperties(_ref, _excluded);

  var innerRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isHover = _useState2[0],
      setIsHover = _useState2[1];

  var onKeyDown = useCallback(function (e) {
    if (e.keyCode === KEY_BACKSPACE && !isDisabled) {
      onDelete();
    }
  }, [isDisabled, onDelete]);
  var onMouseEnter = useCallback(function () {
    return setIsHover(true);
  }, []);
  var onMouseLeave = useCallback(function () {
    return setIsHover(false);
  }, []);
  var iconColor = isDisabled || isError ? 'labelCaption' : isHover ? 'ctrl' : 'label';
  var textColor = isDisabled || isError ? 'labelCaption' : 'label';
  useEffect(function () {
    if (shouldFocus) {
      innerRef.current.focus();
    }
  });
  return /*#__PURE__*/React.createElement(ChipWrapper, _extends({
    tabIndex: "0",
    onKeyDown: onKeyDown,
    ref: useCombinedRefs(ref, innerRef),
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    padding: isDisabled ? '10px' : '5px 10px'
  }, props), /*#__PURE__*/React.createElement(Text, {
    color: textColor
  }, title), !isDisabled && /*#__PURE__*/React.createElement(CloseButton, _extends({
    type: "button",
    tabIndex: "-1"
  }, closeButtonProps, {
    onClick: onDelete
  }), /*#__PURE__*/React.createElement(CloseIcon, {
    color: iconColor,
    size: "7px",
    height: "7px"
  })));
});

var isOptionVisibleDefault = function isOptionVisibleDefault(input, child) {
  if (!child || !child.props) return false;
  return String(child.props.value).toLowerCase().includes(input) || String(child.props.children).toLowerCase().includes(input);
};
/**
 * Input field for enter new Chip
 */


var InputField = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(InputWrapper, null, /*#__PURE__*/React.createElement(StyledInput, _extends({
    type: "text",
    ref: ref
  }, props)));
});
/**
 * ChipsInput component
 */

var _ref4 = /*#__PURE__*/React.createElement(MenuList.HR, null);

export var Chips = withTheme(function (_ref2) {
  var name = _ref2.name,
      _ref2$value = _ref2.value,
      initialValue = _ref2$value === void 0 ? [] : _ref2$value,
      defaultValue = _ref2.defaultValue,
      _ref2$allowDuplicates = _ref2.allowDuplicates,
      allowDuplicates = _ref2$allowDuplicates === void 0 ? false : _ref2$allowDuplicates,
      onChange = _ref2.onChange,
      _ref2$inputProps = _ref2.inputProps,
      inputProps = _ref2$inputProps === void 0 ? {} : _ref2$inputProps,
      children = _ref2.children,
      _ref2$ChipComponent = _ref2.ChipComponent,
      ChipComponent = _ref2$ChipComponent === void 0 ? Chip : _ref2$ChipComponent,
      _ref2$chipProps = _ref2.chipProps,
      chipProps = _ref2$chipProps === void 0 ? {} : _ref2$chipProps,
      _ref2$autocompleteOnl = _ref2.autocompleteOnly,
      autocompleteOnly = _ref2$autocompleteOnl === void 0 ? false : _ref2$autocompleteOnl,
      autocompleteExtraItems = _ref2.autocompleteExtraItems,
      _ref2$placeholder = _ref2.placeholder,
      placeholder = _ref2$placeholder === void 0 ? DEFAULT_PLACEHOLDER : _ref2$placeholder,
      _ref2$menuListProps = _ref2.menuListProps,
      menuListProps = _ref2$menuListProps === void 0 ? {} : _ref2$menuListProps,
      _ref2$dropdownProps = _ref2.dropdownProps,
      dropdownProps = _ref2$dropdownProps === void 0 ? {} : _ref2$dropdownProps,
      _ref2$isOptionVisible = _ref2.isOptionVisible,
      isOptionVisible = _ref2$isOptionVisible === void 0 ? isOptionVisibleDefault : _ref2$isOptionVisible,
      chipTitleRender = _ref2.chipTitleRender,
      _ref2$isChipDisabled = _ref2.isChipDisabled,
      isChipDisabled = _ref2$isChipDisabled === void 0 ? noop : _ref2$isChipDisabled,
      _ref2$noResultsLabel = _ref2.noResultsLabel,
      noResultsLabel = _ref2$noResultsLabel === void 0 ? "No Results" : _ref2$noResultsLabel,
      _ref2$showDropdownIfN = _ref2.showDropdownIfNoResults,
      showDropdownIfNoResults = _ref2$showDropdownIfN === void 0 ? false : _ref2$showDropdownIfN,
      _ref2$addOnBlur = _ref2.addOnBlur,
      addOnBlur = _ref2$addOnBlur === void 0 ? false : _ref2$addOnBlur,
      delimiter = _ref2.delimiter,
      theme = _ref2.theme,
      _ref2$multiSelect = _ref2.multiSelect,
      multiSelect = _ref2$multiSelect === void 0 ? false : _ref2$multiSelect,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isChipFocus = _useState4[0],
      setIsChipFocus = _useState4[1];

  var _useState5 = useState([]),
      _useState6 = _slicedToArray(_useState5, 2),
      bouncingChipIndexes = _useState6[0],
      setBouncingChipIndexes = _useState6[1];

  var _useState7 = useState(defaultValue || ''),
      _useState8 = _slicedToArray(_useState7, 2),
      inputValue = _useState8[0],
      setInputValue = _useState8[1];

  var inputRef = useRef();

  var _useMemo = useMemo(function () {
    var map = {};
    var filtered = [];
    var input = inputValue.toLowerCase();
    React.Children.toArray(children).forEach(function (child) {
      map[child.props.value] = child.props.children;

      if (isFunction(isOptionVisible)) {
        if (isOptionVisible(input, child)) {
          filtered.push(child);
        }
      }
    });
    return [map, filtered];
  }, [children, inputValue, isOptionVisible]),
      _useMemo2 = _slicedToArray(_useMemo, 2),
      autocompleteMap = _useMemo2[0],
      filteredChildren = _useMemo2[1];

  var _useAutocompleteState = useAutocompleteStates(filteredChildren, inputValue),
      isAutocompleteHidden = _useAutocompleteState.hidden,
      focusedOption = _useAutocompleteState.focusedOption,
      showAutocomplete = _useAutocompleteState.showAutocomplete,
      hideAutocomplete = _useAutocompleteState.hideAutocomplete,
      moveDownAutocomplete = _useAutocompleteState.moveDownAutocomplete,
      moveUpAutocomplete = _useAutocompleteState.moveUpAutocomplete;

  var onChipAdded = useCallback(function (newValues) {
    onChange(newValues);
    setInputValue('');
    inputRef.current.focus();
    if (!multiSelect) hideAutocomplete();
  }, [onChange, hideAutocomplete, multiSelect]);
  var onChipRemoved = useCallback(function (newValues) {
    onChange(newValues);
  }, [onChange]);
  var onChipAlreadyAdded = useCallback(function (indexes) {
    setBouncingChipIndexes(indexes);
    setTimeout(function () {
      return setBouncingChipIndexes([]);
    }, 300);
    setInputValue('');
    inputRef.current.focus();
    if (!multiSelect) hideAutocomplete();
  }, [hideAutocomplete, multiSelect]);

  var _useChipValues = useChipValues(initialValue, allowDuplicates, onChipAdded, onChipAlreadyAdded, onChipRemoved, delimiter),
      values = _useChipValues.values,
      addChip = _useChipValues.addChip,
      removeChip = _useChipValues.removeChip;

  var focusOnLastChip = useCallback(function () {
    if (values.length > 0) {
      setIsChipFocus(true);
    }
  }, [setIsChipFocus, values]);
  var selectAutocompleteOption = useCallback(function (index) {
    if (filteredChildren[index]) {
      addChip(filteredChildren[index].props.value);
    }
  }, [filteredChildren, addChip]);
  var handleInputKeyDown = useCallback(function (e) {
    switch (e.keyCode) {
      case KEY_ENTER:
        if (!isAutocompleteHidden && focusedOption > -1) {
          e.preventDefault();
          selectAutocompleteOption(focusedOption);
        } else if (!autocompleteOnly && inputValue !== null && inputValue !== void 0 && inputValue.trim()) {
          e.preventDefault();
          addChip(inputValue);
        }

        break;

      case KEY_ESC:
        hideAutocomplete();
        break;

      case KEY_BACKSPACE:
        if (inputValue === '') {
          focusOnLastChip();
        }

        break;

      case KEY_DOWN:
        e.preventDefault();

        if (isAutocompleteHidden) {
          showAutocomplete();
        } else {
          moveDownAutocomplete();
        }

        break;

      case KEY_UP:
        e.preventDefault();

        if (isAutocompleteHidden) {
          showAutocomplete();
        } else {
          moveUpAutocomplete();
        }

        break;

      default:
        showAutocomplete();
    }

    if (isFunction(inputProps.onKeyDown)) {
      inputProps.onKeyDown(e);
    }
  }, [isAutocompleteHidden, focusedOption, addChip, focusOnLastChip, hideAutocomplete, inputValue, moveDownAutocomplete, moveUpAutocomplete, selectAutocompleteOption, showAutocomplete, autocompleteOnly, inputProps.onKeyDown]);
  var handleInputChange = useCallback(function (event) {
    var value = event.target.value;
    setInputValue(value);

    if (isFunction(inputProps.onChange)) {
      inputProps.onChange(value);
    }
  }, [inputProps.onChange]);
  var handleInputBlur = useCallback(function (event) {
    if (addOnBlur) {
      addChip(inputValue);
    }

    hideAutocomplete();

    if (isFunction(inputProps.onBlur)) {
      inputProps.onBlur(event);
    }
  }, [hideAutocomplete, inputProps.onBlur, addOnBlur, inputValue]);
  var handleInputFocus = useCallback(function (event) {
    showAutocomplete();

    if (isFunction(inputProps.onFocus)) {
      inputProps.onFocus(event);
    }
  }, [showAutocomplete, inputProps.onFocus]);
  var handleInputClick = useCallback(function () {
    showAutocomplete();

    if (isFunction(inputProps.onClick)) {
      inputProps.onClick.apply(inputProps, arguments);
    }
  }, [showAutocomplete, inputProps.onClick]);
  var handleChipBlur = useCallback(function () {
    return setIsChipFocus(false);
  }, [setIsChipFocus]);
  var handleChipDelete = useCallback(function (chip) {
    return function () {
      return removeChip(chip);
    };
  }, [removeChip]);
  var handleAutocompleteItemClick = useCallback(function (value) {
    return function () {
      return addChip(value);
    };
  }, [addChip]);
  return /*#__PURE__*/React.createElement(BaseDropdown, _extends({
    hidden: isDisabled(props) || isAutocompleteHidden || !showDropdownIfNoResults && filteredChildren.length === 0,
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    },
    positionFixed: true,
    target: function target(_ref3) {
      var ref = _ref3.ref;
      return /*#__PURE__*/React.createElement(ChipsWrapper, _extends({
        ref: ref
      }, props), values.map(function (chip, index) {
        return /*#__PURE__*/React.createElement(ChipComponent, _extends({
          key: index,
          id: chip,
          title: isFunction(chipTitleRender) ? chipTitleRender(chip) : autocompleteMap[chip] || chip,
          shouldFocus: isChipFocus && index === values.length - 1,
          isDisabled: isChipDisabled(chip) || isDisabled(props),
          isError: isError(props),
          onDelete: handleChipDelete(chip),
          onBlur: handleChipBlur,
          bounce: bouncingChipIndexes.includes(index)
        }, chipProps));
      }), /*#__PURE__*/React.createElement(InputField, _extends({}, inputProps, {
        ref: inputRef,
        name: name,
        value: inputValue,
        placeholder: values.length > 0 ? inputValue : placeholder,
        disabled: isDisabled(props),
        onKeyDown: handleInputKeyDown,
        onChange: handleInputChange,
        onBlur: handleInputBlur,
        onFocus: handleInputFocus,
        onClick: handleInputClick
      })));
    }
  }, dropdownProps), /*#__PURE__*/React.createElement(MenuList, menuListProps, autocompleteExtraItems ? /*#__PURE__*/React.createElement(React.Fragment, null, autocompleteExtraItems, _ref4) : null, filteredChildren.length ? filteredChildren.map(function (child, index) {
    return /*#__PURE__*/React.createElement(MenuList.Item, _extends({
      key: index,
      disabled: !!child.props.disabled,
      onClick: handleAutocompleteItemClick(child.props.value)
    }, focusedOption === index ? {
      bg: 'navBg'
    } : {}), child.props.children);
  }) : noResultsLabel ? /*#__PURE__*/React.createElement(Text, {
    color: "labelPlaceholder",
    px: theme.menuList.paddingX,
    textAlign: "center"
  }, noResultsLabel) : null));
});